/**
 * CompetitionOffline.jsx (iter231) — Faithful "Internet failure" demo.
 *
 * Judges toggle offline, tap PICK / RECEIVE / COUNT / MOVE actions which
 * queue in localStorage under `stockaris.demo.offlineQueue`, then hit
 * RESTORE CONNECTION which POSTs the queue to /api/competition/demo/
 * offline-sync. The endpoint persists into real movements / cycle_counts
 * / receipts so the judge can verify the queued work landed.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  WifiSlash, WifiHigh, Warning, ArrowClockwise, Package, Truck,
  MagnifyingGlass, ArrowsHorizontal, CheckCircle, Lightning, ArrowLeft,
} from '@phosphor-icons/react';
import { syncCompetitionOfflineActions, trackCompetitionEvent } from '../lib/api';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'stockaris.demo.offlineQueue';

const ACTIONS = [
  { kind: 'pick',    label: 'Pick order line',  hint: 'Simulate scanning a location + SKU',    icon: Package,          tone: 'amber',   qty: 3 },
  { kind: 'receive', label: 'Receive pallet',   hint: 'PO line arriving on dock',              icon: Truck,            tone: 'sky',     qty: 12 },
  { kind: 'count',   label: 'Cycle count',      hint: 'Location count while offline',          icon: MagnifyingGlass,  tone: 'emerald', qty: 50 },
  { kind: 'move',    label: 'Move stock',       hint: 'Consolidate two locations',             icon: ArrowsHorizontal, tone: 'red',     qty: 5 },
];

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
  catch { return []; }
}
function saveQueue(q) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(q));
}

export default function CompetitionOffline() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [online, setOnline] = useState(true);
  const [queue, setQueue] = useState(loadQueue);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    trackCompetitionEvent('demo.offline_page_viewed').catch(() => {});
  }, []);

  // Guard — this page is only useful for demo tenants.
  useEffect(() => {
    if (user && user.company && user.company.is_demo === false) {
      navigate('/');
    }
  }, [user, navigate]);

  const goOffline = () => {
    setOnline(false);
    trackCompetitionEvent('demo.offline_simulated').catch(() => {});
    toast.warning('Internet dropped. Warehouse activity keeps going.', {
      description: 'Actions queue locally until you restore the connection.',
      duration: 4000,
    });
  };

  const queueAction = (kind, qty) => {
    if (online) {
      toast.info('You are online — the action would post live. Toggle offline first.');
      return;
    }
    const item = {
      kind, quantity: qty,
      queued_at: new Date().toISOString(),
      reference: `OFFLINE-${Date.now().toString(36).toUpperCase()}`,
    };
    const next = [...queue, item];
    setQueue(next);
    saveQueue(next);
  };

  const removeQueued = (i) => {
    const next = queue.filter((_, idx) => idx !== i);
    setQueue(next); saveQueue(next);
  };

  const restore = async () => {
    setSyncing(true);
    trackCompetitionEvent('demo.offline_restore_clicked', 'offline_simulation',
      { pending: queue.length }).catch(() => {});
    // Small visible delay so the "Synchronising…" text is readable.
    await new Promise((r) => setTimeout(r, 1200));
    try {
      if (queue.length === 0) {
        setOnline(true);
        setLastSync(null);        // clear stale sync card from prior run
        toast.success('Back online — no queued transactions.');
        return;
      }
      const r = await syncCompetitionOfflineActions(queue);
      setLastSync(r.data);
      setOnline(true);
      setQueue([]); saveQueue([]);
      toast.success(r.data?.message || `${r.data?.synced_total || 0} transactions synchronised.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  const clearQueue = () => {
    if (!window.confirm('Discard all pending offline actions?')) return;
    setQueue([]); saveQueue([]);
  };

  const totalQty = useMemo(() => queue.reduce((a, b) => a + (b.quantity || 0), 0), [queue]);

  return (
    <Layout>
      <div className="space-y-4 p-1" data-testid="competition-offline">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-red-600 mb-1">
              <WifiSlash size={14} weight="duotone" /> Scenario · Internet Failure
            </div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight">
              What happens when the Wi-Fi disappears?
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              The warehouse doesn't stop when the internet drops. Stockaris keeps the picker, the receiver and the counter productive — then syncs everything the moment the network's back.
            </p>
          </div>
          <Button onClick={() => navigate('/competition-demo/dashboard')} variant="outline"
            className="rounded-none uppercase tracking-widest text-xs">
            <ArrowLeft size={14} className="mr-1" /> Back to challenge
          </Button>
        </div>

        {/* Big status banner */}
        {online ? (
          <Card className="p-5 rounded-none border-l-4 border-emerald-500 bg-emerald-50 flex items-center gap-4 flex-wrap"
                data-testid="co-online-banner">
            <WifiHigh size={32} weight="duotone" className="text-emerald-600" />
            <div className="flex-1">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-emerald-700">Online</div>
              <div className="text-lg font-black">Everything runs live. Tap the button to pull the plug.</div>
            </div>
            <Button onClick={goOffline}
              className="rounded-none bg-red-500 hover:bg-red-600 text-white font-black uppercase tracking-widest text-xs py-5 px-6"
              data-testid="co-simulate-failure-btn">
              <WifiSlash size={16} className="mr-2" /> Simulate Internet Failure
            </Button>
          </Card>
        ) : (
          <Card className="p-5 rounded-none border-l-4 border-red-500 bg-red-50 flex items-center gap-4 flex-wrap"
                data-testid="co-offline-banner">
            <WifiSlash size={32} weight="duotone" className="text-red-600" />
            <div className="flex-1">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-red-700">Offline Mode</div>
              <div className="text-lg font-black">Warehouse floor keeps working. {queue.length} action(s) queued locally.</div>
            </div>
            <Button onClick={restore} disabled={syncing}
              className="rounded-none bg-emerald-500 hover:bg-emerald-600 text-white font-black uppercase tracking-widest text-xs py-5 px-6"
              data-testid="co-restore-btn">
              {syncing ? (<><ArrowClockwise size={16} className="mr-2 animate-spin" /> Synchronising warehouse activity…</>) : (
                <><WifiHigh size={16} className="mr-2" /> Restore Connection</>
              )}
            </Button>
          </Card>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="co-actions-grid">
          {ACTIONS.map((a) => {
            const Icon = a.icon;
            return (
              <Card key={a.kind} className={`p-4 rounded-none border-l-4 border-${a.tone}-500 hover:shadow-lg transition`}>
                <Icon size={22} weight="duotone" className={`text-${a.tone}-500 mb-2`} />
                <div className="text-sm font-black">{a.label}</div>
                <div className="text-[10px] text-muted-foreground mt-1 mb-3">{a.hint}</div>
                <Button size="sm"
                  disabled={online}
                  onClick={() => queueAction(a.kind, a.qty)}
                  className={`w-full rounded-none uppercase tracking-widest text-xs font-black bg-${a.tone}-500 hover:bg-${a.tone}-600`}
                  data-testid={`co-action-${a.kind}`}
                >
                  Queue {a.kind} · {a.qty} units
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Queue */}
        <Card className="p-4 rounded-none">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-muted-foreground">
              <Lightning size={12} /> Pending offline queue
              <Badge className={`rounded-none text-[10px] font-black tracking-widest ${queue.length ? 'bg-red-500' : 'bg-neutral-300 text-neutral-700'}`} data-testid="co-queue-count">
                {queue.length}
              </Badge>
              {queue.length > 0 && <span className="text-muted-foreground text-[11px]">· {totalQty} total units</span>}
            </div>
            {queue.length > 0 && (
              <Button size="sm" variant="ghost" onClick={clearQueue}
                className="text-xs uppercase tracking-widest text-red-600" data-testid="co-queue-clear">
                Discard queue
              </Button>
            )}
          </div>
          {queue.length === 0 ? (
            <div className="text-xs italic text-muted-foreground py-4 text-center" data-testid="co-queue-empty">
              {online ? 'Tap Simulate Internet Failure, then queue a few actions.' : 'Queue is empty. Queue something above.'}
            </div>
          ) : (
            <ul className="space-y-1" data-testid="co-queue-list">
              {queue.map((q, i) => (
                <li key={q.reference} className="flex items-center gap-2 border-b last:border-0 py-1 text-[12px]">
                  <span className="w-16 text-[10px] uppercase tracking-widest font-black text-neutral-500">{q.kind}</span>
                  <span className="tabular-nums text-neutral-700">{q.quantity} units</span>
                  <span className="font-mono text-neutral-500 flex-1">{q.reference}</span>
                  <span className="text-neutral-400 text-[10px]">{new Date(q.queued_at).toLocaleTimeString()}</span>
                  <button onClick={() => removeQueued(i)}
                    className="text-neutral-400 hover:text-red-600 text-xs"
                    data-testid={`co-queue-remove-${i}`}>×</button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Last sync result */}
        {lastSync && (
          <Card className="p-4 rounded-none border-l-4 border-emerald-500 bg-emerald-50"
                data-testid="co-sync-result">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-emerald-700 mb-1">
              <CheckCircle size={14} weight="fill" /> Synchronised
            </div>
            <div className="text-lg font-black" data-testid="co-sync-total">
              {lastSync.synced_total} transactions synchronised.
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {Object.entries(lastSync.by_kind || {}).map(([k, v]) => (
                v > 0 && (
                  <Badge key={k} className="rounded-none uppercase tracking-widest text-[10px] bg-white text-emerald-700 border border-emerald-500">
                    {k}: {v}
                  </Badge>
                )
              ))}
            </div>
            <div className="text-[11px] text-muted-foreground mt-3">
              These are real inserts into <code>movements</code>, <code>receipts</code> and <code>cycle_counts</code> tagged <code>source=offline_replay</code> — they appear on the regular Stockaris screens.
            </div>
          </Card>
        )}

        <Card className="p-3 rounded-none border-dashed border-2 bg-neutral-50">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Warning size={14} className="text-amber-500" />
            This is a demo of the Stockaris offline concept — production offline uses a service-worker + IndexedDB pipeline that keeps every warehouse floor tool available during outages.
          </div>
        </Card>
      </div>
    </Layout>
  );
}
